import { useQuery, useMutation } from '@tanstack/react-query'
import { get, post } from './client'
import type {
  Cluster,
  ClusterDetail,
  Gene,
  Protein,
  ReactionResult,
  Statistics,
  BackendStatsResponse,
  PaginatedResponse,
  ApiPaginatedResponse,
} from '../types'

// Helper to transform API paginated response to frontend format
function transformPaginatedResponse<T>(response: ApiPaginatedResponse<T>): PaginatedResponse<T> {
  return {
    items: response.data,
    total: response.meta.total,
    page: response.meta.page,
    limit: response.meta.per_page,
    total_pages: response.meta.total_pages,
  }
}

// Statistics - maps backend /stats response to frontend Statistics interface
export function useStatistics() {
  return useQuery({
    queryKey: ['statistics'],
    queryFn: async () => {
      const response = await get<BackendStatsResponse>('/stats')
      return {
        total_clusters: response.total_clusters,
        total_genes: response.total_genes,
        total_structures: response.total_pdb_hits,
        total_compounds: response.total_swissprot_hits,
        total_reactions: response.total_reactions,
        cluster_types: response.biosyn_class_distribution,
        organisms: response.organism_distribution,
      } as Statistics
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// Clusters
export function useClusters(params: {
  page?: number
  limit?: number
  search?: string
  cluster_type?: string
  organism?: string
}) {
  // Map frontend param names to backend param names
  const apiParams = {
    page: params.page,
    per_page: params.limit,
    search: params.search,
    biosyn_class: params.cluster_type,
    organism: params.organism,
  }

  return useQuery({
    queryKey: ['clusters', params],
    queryFn: async () => {
      const response = await get<ApiPaginatedResponse<Cluster>>('/clusters', apiParams)
      return transformPaginatedResponse(response)
    },
  })
}

// API response for single cluster detail
interface ClusterDetailResponse {
  cluster: ClusterDetail
  genes: Gene[]
}

export function useCluster(clusterId: string) {
  return useQuery({
    queryKey: ['cluster', clusterId],
    queryFn: async () => {
      const response = await get<ClusterDetailResponse>(`/clusters/${clusterId}`)
      // Transform response to extract cluster with genes included
      return {
        ...response.cluster,
        genes: response.genes,
      } as ClusterDetail
    },
    enabled: !!clusterId,
  })
}

// Interface for cluster filters response
interface ClusterFiltersResponse {
  biosyn_classes: string[]
  organism_names: string[]
}

export function useClusterFilters() {
  return useQuery({
    queryKey: ['cluster-filters'],
    queryFn: () => get<ClusterFiltersResponse>('/clusters/filters'),
    staleTime: 30 * 60 * 1000, // 30 minutes
  })
}

// Legacy hooks that use the new filters endpoint
export function useClusterTypes() {
  const { data, ...rest } = useClusterFilters()
  return {
    data: data?.biosyn_classes,
    ...rest,
  }
}

export function useOrganisms() {
  const { data, ...rest } = useClusterFilters()
  return {
    data: data?.organism_names,
    ...rest,
  }
}

// Genes
export function useGene(geneId: string) {
  return useQuery({
    queryKey: ['gene', geneId],
    queryFn: () => get<Gene>(`/genes/${geneId}`),
    enabled: !!geneId,
  })
}

export function useGenesByCluster(clusterId: string) {
  return useQuery({
    queryKey: ['genes', 'cluster', clusterId],
    queryFn: () => get<Gene[]>(`/genes/cluster/${clusterId}`),
    enabled: !!clusterId,
  })
}

// Proteins - backend uses path params: /proteins/{mibig_accession}/{protein_id}
export function useProtein(proteinId: string, mibigAccession?: string) {
  return useQuery({
    queryKey: ['protein', proteinId, mibigAccession],
    queryFn: () => {
      if (mibigAccession) {
        return get<Protein>(`/proteins/${mibigAccession}/${proteinId}`)
      }
      // Fallback for legacy calls without mibig_accession (should not happen)
      return get<Protein>(`/proteins/${proteinId}`)
    },
    enabled: !!proteinId && !!mibigAccession,
  })
}

// Protein PDB BLAST hits (supports domain-level filtering with res_start/res_end)
export function useProteinPdbHits(
  proteinId: string,
  mibigAccession: string,
  resStart?: number,
  resEnd?: number
) {
  return useQuery({
    queryKey: ['protein-pdb-hits', proteinId, mibigAccession, resStart, resEnd],
    queryFn: () => {
      let url = `/proteins/${mibigAccession}/${proteinId}/pdb-hits`
      const params = []
      if (resStart !== undefined && resEnd !== undefined) {
        params.push(`res_start=${resStart}`, `res_end=${resEnd}`)
      }
      if (params.length > 0) {
        url += `?${params.join('&')}`
      }
      return get(url)
    },
    enabled: !!proteinId && !!mibigAccession,
  })
}

// Protein SwissProt BLAST hits (supports domain-level filtering with res_start/res_end and rhea filter)
export function useProteinSwissprotHits(
  proteinId: string,
  mibigAccession: string,
  resStart?: number,
  resEnd?: number,
  rheaAccession?: string
) {
  return useQuery({
    queryKey: ['protein-swissprot-hits', proteinId, mibigAccession, resStart, resEnd, rheaAccession],
    queryFn: () => {
      let url = `/proteins/${mibigAccession}/${proteinId}/swissprot-hits`
      const params = []
      if (resStart !== undefined && resEnd !== undefined) {
        params.push(`res_start=${resStart}`, `res_end=${resEnd}`)
      }
      if (rheaAccession) {
        params.push(`rhea_accession=${encodeURIComponent(rheaAccession)}`)
      }
      if (params.length > 0) {
        url += `?${params.join('&')}`
      }
      return get(url)
    },
    enabled: !!proteinId && !!mibigAccession,
  })
}

export function useProteinStructure(geneId: string, source: 'alphafold' | 'colabfold') {
  return useQuery({
    queryKey: ['protein-structure', geneId, source],
    queryFn: () => get<{ pdb_content: string }>(`/structures/${source}/${geneId}`),
    enabled: !!geneId,
  })
}

// BLAST Search
export function useBlastSearch() {
  return useMutation({
    mutationFn: (data: {
      query: string
      database: 'mibig' | 'pdbaa' | 'swissprot' | 'Cdd'
      evalue?: number
      max_sequences?: number
    }) => post<{ job_id: string }>('/blast', data),
  })
}

// BLAST result types matching backend schema
export interface BlastMibigHit {
  hit_num: number
  hsp_num: number
  evalue: number
  identity: number
  cover: number
  title?: string
  mibig_accession: string
  protein_id: string
  product?: string
  organism_name?: string
  pdb_hit_count: number
  swissprot_hit_count: number
  fujisan_hit_count: number
  unkai_hit_count: number
}

export interface BlastPdbHit {
  hit_num: number
  hsp_num: number
  evalue: number
  identity: number
  cover: number
  title?: string
  pdb_id: string
  chain_id?: string  // Comma-separated for multiple chains
  chain_ids: string[]  // List of chain IDs
  ligands: string[]
}

export interface ReactionEquation {
  equation: string  // Formatted equation string
  rhea_id: string  // RHEA accession (just the number)
  ec_number?: string
  cofactors: string[]
}

export interface BlastSwissprotHit {
  hit_num: number
  hsp_num: number
  evalue: number
  identity: number
  cover: number
  title?: string
  swissprot_accession: string
  recname?: string
  ec_numbers: string[]
  kegg_ids: string[]
  reactions: ReactionEquation[]  // Full reaction equations
}

export interface BlastCddHit {
  pssm_id: string
  accession: string
  short_name: string
  description?: string
  res_start: number
  res_end: number
  evalue: number
  bitscore: number
}

export interface BlastCddSite {
  site_type: string
  residues: string[]
  source_domain: string
  seq_length: number
}

export interface AggregatedLigand {
  ligand_id: string
  count: number
  best_evalue: number
  name?: string
  formula?: string
  smiles?: string
  pubchem_cid?: string
}

export interface AggregatedChebi {
  chebi_id: string
  count: number
  best_evalue: number
  side: number  // 0 = substrate (left), 1 = product (right)
  name?: string
  formula?: string
  smiles?: string
  pubchem_cid?: string
}

export interface BlastResultData {
  job_id: string
  status: 'queued' | 'processing' | 'complete' | 'failed'
  database: string
  query_length: number
  total_hits: number
  mibig_hits: BlastMibigHit[]
  pdb_hits: BlastPdbHit[]
  swissprot_hits: BlastSwissprotHit[]
  cdd_domains: BlastCddHit[]
  cdd_sites: BlastCddSite[]
  aggregated_ligands: AggregatedLigand[]
  aggregated_chebi: AggregatedChebi[]
  download_url?: string
}

export function useBlastResults(jobId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['blast-results', jobId],
    queryFn: () => get<BlastResultData>(`/blast/${jobId}`),
    enabled: enabled && !!jobId,
    refetchInterval: (query) => {
      const data = query.state?.data
      if (!data) return 2000
      if (data.status === 'queued' || data.status === 'processing') return 2000
      return false
    },
  })
}

// Reaction Search - 3-level navigation

// Level 1: Search compounds by name
export interface CompoundSearchResult {
  chebi_accession: string
  name?: string
  formula?: string
  smiles?: string
  structure_url?: string
}

export function useCompoundSearch(text: string) {
  return useQuery({
    queryKey: ['compound-search', text],
    queryFn: () => get<{ success: boolean; data: CompoundSearchResult[]; total: number }>(`/reactions/search?text=${encodeURIComponent(text)}`),
    enabled: !!text && text.length > 0,
  })
}

// Level 2: Get reactions for a compound
export interface CompoundReaction {
  rhea_id: string
  equation?: string
  gene_count: number
  rhea_url: string
}

export function useCompoundReactions(chebiAccession: string) {
  return useQuery({
    queryKey: ['compound-reactions', chebiAccession],
    queryFn: () => get<{ success: boolean; data: CompoundReaction[]; total: number }>(`/reactions/compounds/${chebiAccession}`),
    enabled: !!chebiAccession,
  })
}

// Level 3: Get proteins for a reaction
export interface ReactionProtein {
  mibig_accession: string
  protein_id: string
  hit_num: number
  evalue: number
  organism_name?: string
  ncbi_tax_id?: string
  product?: string
  swissprot_hit_count?: number
}

export function useReactionProteins(rheaAccession: string, chebiAccession: string) {
  return useQuery({
    queryKey: ['reaction-proteins', rheaAccession, chebiAccession],
    queryFn: () => get<{ success: boolean; data: ReactionProtein[]; total: number }>(`/reactions/reactions/${rheaAccession}/proteins?chebi_accession=${chebiAccession}`),
    enabled: !!rheaAccession && !!chebiAccession,
  })
}

// Legacy hook (still used for simple search)
export function useReactionSearch(params: {
  query?: string
  page?: number
  limit?: number
}) {
  return useQuery({
    queryKey: ['reactions', params],
    queryFn: () => get<PaginatedResponse<ReactionResult>>('/reactions/search', params),
    enabled: !!params.query,
  })
}

// Substructure Search - uses SMARTS patterns
export function useSubstructureSearch() {
  return useMutation({
    mutationFn: (data: { smarts: string }) =>
      post<{ job_id: string; status: string }>('/substructure/search', data),
  })
}

export interface SubstructureMatch {
  chebi_id: string
  smiles: string
  name?: string
  formula?: string
  mibig_count: number
  rhea_count: number
  structure_url: string
}

export interface SubstructureResultsData {
  job_id: string
  status: 'queued' | 'processing' | 'complete' | 'failed'
  error?: string
  data?: SubstructureMatch[]
  page?: number
  per_page?: number
  total?: number
  total_pages?: number
}

export function useSubstructureResults(jobId: string, page: number = 1, enabled: boolean = true) {
  return useQuery({
    queryKey: ['substructure-results', jobId, page],
    queryFn: () => get<SubstructureResultsData>(`/substructure/${jobId}?page=${page}&per_page=50`),
    enabled: enabled && !!jobId,
    staleTime: 0,  // Always fetch fresh data immediately when enabled
    refetchOnMount: true,  // Refetch when component mounts
    refetchInterval: (query) => {
      const data = query.state?.data
      if (!data) return 500  // Poll every 500ms initially for faster response
      if (data.status === 'queued' || data.status === 'processing') return 1000  // Poll every 1s while processing
      return false  // Stop polling when complete
    },
  })
}

// Cluster Complex - protein-protein interactions
export interface ProteinInteraction {
  protein1_id: string
  protein1_product: string
  protein2_id: string
  protein2_product: string
  ipsae: number
  ipsae_min: number
  iptm: number
  is_homo: boolean
}

export interface ClusterComplexData {
  mibig_accession: string
  svg_url?: string
  interactions: ProteinInteraction[]
  total: number
}

export function useClusterComplex(clusterId: string) {
  return useQuery({
    queryKey: ['cluster-complex', clusterId],
    queryFn: () => get<ClusterComplexData>(`/clusters/${clusterId}/complex`),
    enabled: !!clusterId,
  })
}

// Complex Detail - for individual protein-protein complex page
export interface ComplexDetailData {
  mibig_accession: string
  protein1_id: string
  protein1_product?: string
  protein2_id: string
  protein2_product?: string
  ipsae: number
  ipsae_min: number
  iptm: number
  is_homo: boolean
  structure_url?: string
}

export function useComplexDetail(clusterId: string, protein1Id: string, protein2Id: string) {
  return useQuery({
    queryKey: ['complex-detail', clusterId, protein1Id, protein2Id],
    queryFn: () => get<ComplexDetailData>(`/clusters/${clusterId}/complex/${protein1Id}/${protein2Id}`),
    enabled: !!clusterId && !!protein1Id && !!protein2Id,
  })
}

// FUJISAN - Function prediction based on structural similarity
export interface FujisanHit {
  rank: number
  recname: string
  uniprot_id: string
  bitscore?: number
  full_seqid?: number
  dmn_rmsd?: number
  dmn_seqid?: number
  dmn_tm?: number
  score: number
  rhea_ids: string[]
  reactions: string[]
  ec_numbers: string[]
  uniprot_url: string
}

export interface ReactionInfo {
  rhea_id: string
  equation?: string
  ec_numbers: string[]
  rhea_url: string
}

export interface CompoundInfo {
  chebi_id: string
  name?: string
  formula?: string
  smiles?: string
  pubchem_cid?: number
  structure_url: string
  chebi_url: string
}

export interface FujisanHitsData {
  success: boolean
  data: FujisanHit[]
  total: number
  unique_reactions: ReactionInfo[]
  unique_compounds: CompoundInfo[]
}

export function useFujisanHits(proteinId: string, mibigAccession: string, limit: number = 50) {
  return useQuery({
    queryKey: ['fujisan-hits', proteinId, mibigAccession, limit],
    queryFn: () => get<FujisanHitsData>(`/proteins/${mibigAccession}/${proteinId}/fujisan-hits?limit=${limit}`),
    enabled: !!proteinId && !!mibigAccession,
  })
}

// UNKAI - Function prediction based on sequence embeddings
export interface UnkaiHit {
  rank: number
  recname: string
  uniprot_id: string
  score: number
  rhea_ids: string[]
  reactions: string[]
  ec_numbers: string[]
  uniprot_url: string
}

export interface UnkaiHitsData {
  success: boolean
  data: UnkaiHit[]
  total: number
  unique_reactions: ReactionInfo[]
  unique_compounds: CompoundInfo[]
}

export function useUnkaiHits(proteinId: string, mibigAccession: string, limit: number = 50) {
  return useQuery({
    queryKey: ['unkai-hits', proteinId, mibigAccession, limit],
    queryFn: () => get<UnkaiHitsData>(`/proteins/${mibigAccession}/${proteinId}/unkai-hits?limit=${limit}`),
    enabled: !!proteinId && !!mibigAccession,
  })
}

// Global Search - for Command Palette (uses unified /search API with fuzzy matching)
interface SearchResult {
  type: 'cluster' | 'protein' | 'compound'
  id: string
  title: string
  subtitle?: string
  url: string
  score: number
}

interface GlobalSearchCluster {
  mibig_accession: string
  organism_name: string
  biosyn_class: string[]
}

interface GlobalSearchProtein {
  mibig_accession: string
  protein_id: string
  product: string
}

interface GlobalSearchCompound {
  mibig_accession: string
  compound: string
  database_id?: string
}

interface GlobalSearchResults {
  clusters: GlobalSearchCluster[]
  proteins: GlobalSearchProtein[]
  compounds: GlobalSearchCompound[]
  total: number
}

interface UnifiedSearchResponse {
  query: string
  total: number
  results: SearchResult[]
  clusters: SearchResult[]
  proteins: SearchResult[]
  compounds: SearchResult[]
}

export function useGlobalSearch(query: string) {
  return useQuery({
    queryKey: ['global-search', query],
    queryFn: async () => {
      // Use the unified smart search API
      const response = await get<UnifiedSearchResponse>('/search', { q: query, limit: 10 })

      // Transform response to match existing frontend interface
      return {
        clusters: response.clusters.map((c) => {
          // Parse subtitle "Organism Name • Class1, Class2"
          const parts = c.subtitle?.split(' • ') || []
          return {
            mibig_accession: c.id,
            organism_name: parts[0] || '',
            biosyn_class: parts[1]?.split(', ').filter(Boolean) || [],
          }
        }),
        proteins: response.proteins.map((p) => {
          // Parse subtitle "BGC0000001 • product description"
          const parts = p.subtitle?.split(' • ') || []
          return {
            mibig_accession: parts[0] || p.id.split('/')[0] || '',
            protein_id: p.title,
            product: parts[1] || '',
          }
        }),
        compounds: response.compounds.map((c) => {
          // Parse subtitle "BGC0000001 • database_id"
          const parts = c.subtitle?.split(' • ') || []
          return {
            mibig_accession: parts[0] || '',
            compound: c.title,
            database_id: parts[1] || '',
          }
        }),
        total: response.total,
      } as GlobalSearchResults
    },
    enabled: query.length >= 2,
    staleTime: 30 * 1000, // 30 seconds
  })
}

// Search suggestions for autocomplete
export function useSearchSuggestions(query: string) {
  return useQuery({
    queryKey: ['search-suggestions', query],
    queryFn: () => get<string[]>('/search/suggestions', { q: query, limit: 10 }),
    enabled: query.length >= 2,
    staleTime: 60 * 1000, // 1 minute
  })
}
